import { isDefined, zipper } from "../utils";
import ema from "./ema";
import sma from "./sma";
import { StochasticMomentum as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const {
      kPeriods,
      kSmoothPeriods,
      kDoubleSmoothPeriods,
      dPeriods,
      dMaType,
    } = options;

    if (data.length < kPeriods) {
      return [];
    }

    // Helper function to get lowest low and highest high over period
    function getLLVHHV(period, index) {
      let low = null;
      let high = null;
      for (let j = index - period + 1; j <= index; j++) {
        if (j < 0 || j >= data.length) continue;
        const bar = data[j];
        if (!isDefined(bar) || !isDefined(bar.low) || !isDefined(bar.high))
          continue;

        low = low === null ? bar.low : Math.min(low, bar.low);
        high = high === null ? bar.high : Math.max(high, bar.high);
      }
      return [low, high];
    }

    // Step 1: Calculate H and DHL for each bar
    const hData = [];
    const dhlData = [];

    for (let i = 0; i < data.length; i++) {
      if (i < kPeriods - 1) {
        hData.push(undefined);
        dhlData.push(undefined);
        continue;
      }

      const bar = data[i];
      if (!isDefined(bar) || !isDefined(bar.close)) {
        hData.push(undefined);
        dhlData.push(undefined);
        continue;
      }

      const [low, high] = getLLVHHV(kPeriods, i);
      if (low === null || high === null) {
        hData.push(undefined);
        dhlData.push(undefined);
        continue;
      }

      const midpoint = (low + high) / 2;
      const h = bar.close - midpoint;
      const dhl = high - low;

      hData.push(h);
      dhlData.push(dhl);
    }

    // Step 2: Apply first EMA smoothing to H and DHL
    const hDataForEMA1 = hData.map((h) =>
      isDefined(h) ? { value: h } : undefined,
    );
    const dhlDataForEMA1 = dhlData.map((dhl) =>
      isDefined(dhl) ? { value: dhl } : undefined,
    );

    const ema1H = ema().options({
      windowSize: kSmoothPeriods,
      sourcePath: "value",
    });
    const ema1DHL = ema().options({
      windowSize: kSmoothPeriods,
      sourcePath: "value",
    });

    const hs1Data = ema1H(hDataForEMA1);
    const dhl1Data = ema1DHL(dhlDataForEMA1);

    // Step 3: Apply second EMA smoothing
    const hs1DataForEMA2 = hs1Data.map((hs1) =>
      isDefined(hs1) ? { value: hs1 } : undefined,
    );
    const dhl1DataForEMA2 = dhl1Data.map((dhl1) =>
      isDefined(dhl1) ? { value: dhl1 } : undefined,
    );

    const ema2H = ema().options({
      windowSize: kDoubleSmoothPeriods,
      sourcePath: "value",
    });
    const ema2DHL = ema().options({
      windowSize: kDoubleSmoothPeriods,
      sourcePath: "value",
    });

    const hs2Data = ema2H(hs1DataForEMA2);
    const dhl2Data = ema2DHL(dhl1DataForEMA2);

    // Step 4: Calculate %K
    const kValues = [];
    for (let i = 0; i < data.length; i++) {
      const hs2 = hs2Data[i];
      const dhl2 = dhl2Data[i];

      if (!isDefined(hs2) || !isDefined(dhl2)) {
        kValues.push(undefined);
        continue;
      }

      // Prevent division by zero
      const denominator = 0.5 * dhl2;
      if (Math.abs(denominator) < 0.00000001) {
        kValues.push(0);
      } else {
        const k = (hs2 / denominator) * 100;
        kValues.push(k);
      }
    }

    // Step 5: Calculate %D (signal line) using specified MA type
    const kDataForD = kValues.map((k) =>
      isDefined(k) ? { value: k } : undefined,
    );

    let dValues;
    if (dMaType === "ema") {
      const emaD = ema().options({ windowSize: dPeriods, sourcePath: "value" });
      dValues = emaD(kDataForD);
    } else {
      const smaD = sma().options({ windowSize: dPeriods, sourcePath: "value" });
      dValues = smaD(kDataForD);
    }

    // Step 6: Combine %K and %D
    const zip = zipper().combine((k, d) => ({
      k: k,
      d: d,
    }));

    const result = zip(kValues, dValues);

    return result;
  }

  calculator.undefinedLength = function () {
    const { kPeriods, kSmoothPeriods, kDoubleSmoothPeriods, dPeriods } =
      options;
    // kPeriods + kSmoothPeriods + kDoubleSmoothPeriods + dPeriods - 3
    return kPeriods + kSmoothPeriods + kDoubleSmoothPeriods + dPeriods - 3;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
